import { z } from "zod";

export const runStateSchema = z.enum(["queued", "running", "done", "failed"]);
export type RunState = z.infer<typeof runStateSchema>;

export const runCategorySchema = z.enum(["primary", "secondary"]);
export type RunCategory = z.infer<typeof runCategorySchema>;

export const runTestStatusSchema = z.enum(["passed", "failed", "info"]);
export type RunTestStatus = z.infer<typeof runTestStatusSchema>;

export const testConsoleLogSchema = z.object({
  level: z.enum(["log", "info", "warning", "error"]),
  msg: z.string(),
});

export const testNetworkEntrySchema = z.object({
  method: z.string(),
  url: z.string().url(),
  status: z.number().int(),
  ms: z.number().int().nonnegative(),
});

export const testNetworkLogsSchema = z.object({
  nav: z.array(testNetworkEntrySchema),
  navMore: z.number().int().nonnegative().optional(),
  api: z.array(testNetworkEntrySchema),
  apiMore: z.number().int().nonnegative().optional(),
  thirdPartyFold: z
    .array(
      z.object({
        host: z.string(),
        calls: z.number().int().nonnegative(),
        failures: z.number().int().nonnegative(),
      })
    )
    .optional(),
});

export const testLogsSchema = z.object({
  console: z.array(testConsoleLogSchema),
  consoleMore: z.number().int().nonnegative().optional(),
  network: testNetworkLogsSchema,
});

export const runTestSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  status: runTestStatusSchema,
  category: runCategorySchema,
  passed: z.boolean().optional(),
  summary: z.string().optional(),
  recommendation: z.string().optional(),
  apiTrace: z.string().optional(),
  code: z.string().optional(),
  logs: testLogsSchema.optional(),
});
export type RunTest = z.infer<typeof runTestSchema>;

export const runTotalsSchema = z.object({
  total: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  warnings: z.number().int().nonnegative(),
  info: z.number().int().nonnegative(),
  primary: z.object({
    total: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    info: z.number().int().nonnegative(),
  }),
  secondary: z.object({
    total: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    warnings: z.number().int().nonnegative(),
    info: z.number().int().nonnegative(),
  }),
});
export type RunTotals = z.infer<typeof runTotalsSchema>;

export const runArtifactSchema = z.object({
  kind: z.enum(["screenshot", "trace", "log"]).catch("log"),
  path: z.string(),
  url: z.string(),
});
export type RunArtifact = z.infer<typeof runArtifactSchema>;

export const runFailedDetailSchema = z.object({
  id: z.string(),
  title: z.string(),
  stepSummary: z.string(),
  console: z.array(z.string()),
  network: z.array(z.string()),
  api: z.array(z.string()),
});
export type RunFailedDetail = z.infer<typeof runFailedDetailSchema>;

export const runSummarySchema = z.object({
  coverage: z.number().int().min(0).max(100),
  totals: runTotalsSchema,
  suiteName: z.string(),
  tool: z.string(),
  triggeredBy: z.string(),
  executedAt: z.string().datetime({ offset: true }),
  duration: z.string(),
  targetUrl: z.string(),
});
export type RunSummary = z.infer<typeof runSummarySchema>;

export const runResultResponseSchema = z.object({
  summary: runSummarySchema,
  tests: z.array(runTestSchema),
  artifacts: z.array(runArtifactSchema),
  failedDetails: z.array(runFailedDetailSchema),
  meta: z
    .object({
      simulated: z.boolean().optional(),
      artifactNotes: z.array(z.string()).optional(),
      abortedReason: z.string().optional(),
    })
    .optional(),
});
export type RunResultResponse = z.infer<typeof runResultResponseSchema>;

export const runStartResponseSchema = z.object({
  runId: z.string().uuid(),
  normalizedUrl: z.string().url(),
  displayHost: z.string(),
  queueSize: z.number().int().nonnegative(),
  queuePending: z.number().int().nonnegative(),
  queueMax: z.number().int().positive(),
  queuePosition: z.number().int().positive(),
});
export type RunStartResponse = z.infer<typeof runStartResponseSchema>;

export const runStatusResponseSchema = z.object({
  runId: z.string().uuid(),
  state: runStateSchema,
  progress: z.number().min(0).max(100),
  progressNote: z.string(),
  queuePosition: z.number().int().positive().nullable(),
  createdAt: z.string().datetime({ offset: true }),
  finishedAt: z.string().datetime({ offset: true }).nullable(),
});
export type RunStatusResponse = z.infer<typeof runStatusResponseSchema>;

export const runErrorCodeSchema = z.enum([
  "url_required",
  "invalid_url",
  "home_only",
  "rate_limited",
  "queue_full",
  "not_found",
  "start_failed",
  "status_failed",
  "result_failed",
  "runner_failed",
  "invalid_target",
]);
export type RunErrorCode = z.infer<typeof runErrorCodeSchema>;

export const runErrorSchema = z.object({
  error: runErrorCodeSchema,
  message: z.string().optional(),
  home: z.string().url().optional(),
  displayHost: z.string().optional(),
  retryAfterMs: z.number().int().nonnegative().optional(),
});
export type RunErrorResponse = z.infer<typeof runErrorSchema>;
